import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import { logger } from '../services/logger';

interface RoleProtectedRouteProps {
  children: ReactNode;
  allowedRoles: string[];
}

export function RoleProtectedRoute({ children, allowedRoles }: RoleProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" label="Authenticating..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    logger.logAuth('Unauthorized access attempt - not authenticated');
    return <Navigate to="/login" replace />;
  }

  // Check if user's role is in the allowed roles list
  const hasAccess = user?.role && allowedRoles.includes(user.role);

  if (!hasAccess) {
    logger.logAuth('Unauthorized access attempt - insufficient privileges', {
      userId: user?.userId,
      userRole: user?.role,
      requiredRoles: allowedRoles,
    });

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
            You do not have permission to access this page. This page requires{' '}
            {allowedRoles.length === 1 ? (
              <span className="font-semibold">{allowedRoles[0]}</span>
            ) : (
              <>
                one of the following roles:{' '}
                <span className="font-semibold">{allowedRoles.join(', ')}</span>
              </>
            )}{' '}
            role.
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              <span className="font-semibold">Your current role:</span> {user?.role || 'Unknown'}
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-2">
              If you believe you should have access, please contact your system administrator.
            </p>
          </div>
          <div className="mt-6 text-center">
            <button
              onClick={() => window.history.back()}
              className="btn-secondary"
              aria-label="Go back to previous page"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  logger.logAuth('Access granted to protected route', {
    userId: user?.userId,
    userRole: user?.role,
  });

  return <>{children}</>;
}
